import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeQuestCheckpoint, updateQuestCheckpoint } from "redux/actions/quest";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  missionId: string;
  checkpointId: string;
}

const initialValues = {
  sequence: 1,
  name: "",
  code: "",
  points: 0,
  is_active: true,
};

const CheckpointFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  missionId,
  checkpointId,
}) => {
  const isAddMode = !checkpointId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const { questCheckpoints, isFormLoading } = useSelector(
    (state: any) => state.questReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handleSubmit = (values: any) => {
    const payload = {
      ...values,
      sequence: Number(values.sequence),
      points: Number(values.points),
    };
    isAddMode
      ? dispatch(storeQuestCheckpoint(missionId, payload, handleCloseModal) as any)
      : dispatch(
          updateQuestCheckpoint(checkpointId, missionId, payload, handleCloseModal) as any
        );
  };

  useEffect(() => {
    if (!isAddMode && isOpen) {
      const detail = questCheckpoints.find(
        (item: any) => String(item.id) === String(checkpointId)
      );
      if (detail && formikRef.current) {
        formikRef.current.setValues({
          sequence: detail.sequence ?? 1,
          name: detail.name ?? "",
          code: detail.code ?? "",
          points: detail.points ?? 0,
          is_active: detail.is_active ?? true,
        });
      }
    } else if (isAddMode && formikRef.current) {
      formikRef.current.resetForm();
    }
  }, [isAddMode, questCheckpoints, checkpointId, isOpen]);

  const validationSchema = yup.object({
    name: yup.string().required("Nama checkpoint wajib diisi"),
    code: yup.string().required("Kode checkpoint wajib diisi"),
    sequence: yup.number().min(1).required("Urutan wajib diisi"),
    points: yup.number().min(0).required("Poin wajib diisi"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <DialogHeader>
              <Text fontWeight={700}>
                {isAddMode ? "Tambah Checkpoint" : "Edit Checkpoint"}
              </Text>
            </DialogHeader>
            <DialogContent>
              <FormWrapper>
                <Field name="sequence">
                  {({ field, meta }: FieldProps<number>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="sequence">Urutan (Sequence)</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="number"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                <Field name="name">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="name">Nama Checkpoint</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          placeholder="contoh: Halilintar"
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                <Field name="code">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="code">Kode Scan</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          placeholder="contoh: HLTR-01"
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                <Field name="points">
                  {({ field, meta }: FieldProps<number>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="points">Poin</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="number"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>
              </FormWrapper>
            </DialogContent>
            <DialogFooter>
              <Stack
                display="flex"
                flexDirection="row"
                spacing="xs"
                flex={1}
                style={{ gap: 15 }}
              >
                <Button
                  variant="outline"
                  block
                  type="button"
                  onClick={() => handleCloseModal()}
                >
                  Batal
                </Button>
                <Button
                  variant="primary"
                  block
                  type="submit"
                  isLoading={isFormLoading}
                >
                  {isAddMode ? "Simpan" : "Perbarui"}
                </Button>
              </Stack>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CheckpointFormModal;
